/**
 * MyMouvEventDetail — full event page with map, tickets, share, report.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, Share2, Flag, Calendar, Clock, MapPin, Ticket, ExternalLink, User, Loader2, Tag, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { getMediaUrl } from '../utils/mediaUrl';
import AccreditationRequestForm from '../components/AccreditationRequestForm';

const currencySymbol = (c) => ({ EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', JPY: '¥', CNY: '¥', INR: '₹', BRL: 'R$', RUB: '₽', KRW: '₩', ARS: '$', XOF: 'FCFA', XAF: 'FCFA' }[c] || c);

const purpleIcon = L.divIcon({
  className: 'mymouv-marker',
  html: '<div style="width:32px;height:32px;background:#a855f7;border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 12px rgba(168,85,247,0.5);"></div>',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const MyMouvEventDetail = () => {
  const { t } = useLanguage();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // ticket_type_id
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoTtypeId, setPromoTtypeId] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [fullscreen, setFullscreen] = useState(null); // { type: 'image'|'video', src }
  const keyboardHeight = useKeyboardHeight();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/mymouv/events/${eventId}`);
      setEv(r.data);
    } catch (e) {
      toast.error('Événement introuvable');
      navigate('/mymouv');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const buy = async (ticketTypeId) => {
    setBuying(ticketTypeId);
    try {
      const r = await axios.post(`/api/mymouv/events/${eventId}/checkout`, { ticket_type_id: ticketTypeId, quantity: 1 });
      if (r.data?.free) {
        toast.success('Billet gratuit émis ! Consultez vos billets.');
        setTimeout(() => navigate('/mymouv/my-tickets'), 1500);
        return;
      }
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBuying(null);
    }
  };

  const share = async () => {
    const url = ev?.public_url || `https://app.destine.studio/mymouv/events/${eventId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: ev?.title, text: ev?.description, url });
      } catch (_e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Lien copié !');
      } catch (_e) {
        toast.error('Impossible de copier');
      }
    }
  };

  const submitReport = async () => {
    if (!reportReason.trim()) { toast.error('Précisez la raison'); return; }
    try {
      await axios.post(`/api/mymouv/events/${eventId}/report`, { reason: reportReason });
      toast.success('Signalement envoyé. Notre équipe va examiner.');
      setReporting(false);
      setReportReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const redeemPromo = async () => {
    if (!promoCode.trim()) { toast.error('Entrez un code promo'); return; }
    if (!promoTtypeId) { toast.error('Choisissez un type de billet'); return; }
    setPromoBusy(true);
    try {
      await axios.post(`/api/mymouv/events/${eventId}/redeem-promo`, {
        code: promoCode.trim().toUpperCase(),
        ticket_type_id: promoTtypeId,
      });
      toast.success('Code promo validé ! Billet gratuit émis.');
      setPromoOpen(false);
      setPromoCode('');
      setTimeout(() => navigate('/mymouv/my-tickets'), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code promo invalide');
    } finally {
      setPromoBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!ev) return null;

  const hasCoords = ev.location?.lat && ev.location?.lng;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mymouv-event-detail">
      {/* Cover */}
      <div className="relative bg-gradient-to-br from-purple-900 to-fuchsia-800 overflow-hidden">
        {ev.cover_image_url ? (
          <button
            type="button"
            onClick={() => setFullscreen({ type: 'image', src: getMediaUrl(ev.cover_image_url) })}
            className="block w-full"
            data-testid="mymouv-event-cover-btn"
          >
            <img
              src={getMediaUrl(ev.cover_image_url)}
              alt={ev.title}
              className="w-full max-h-[75vh] object-contain bg-black"
              data-testid="mymouv-event-cover-img"
            />
          </button>
        ) : (
          <div className="aspect-video md:h-64" />
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-black/60" />
        <button onClick={() => navigate('/mymouv')} className="absolute top-4 left-4 p-2.5 rounded-full bg-black/50 hover:bg-black/70 z-10" data-testid="mymouv-event-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button onClick={share} className="p-2.5 rounded-full bg-black/50 hover:bg-black/70" data-testid="mymouv-event-share">
            <Share2 className="w-5 h-5" />
          </button>
          <button onClick={() => setReporting(true)} className="p-2.5 rounded-full bg-black/50 hover:bg-black/70" data-testid="mymouv-event-report">
            <Flag className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="rounded-3xl bg-gradient-to-br from-purple-900/30 to-fuchsia-900/20 border border-fuchsia-500/30 p-6">
          <h1 className="text-3xl font-bold" data-testid="mymouv-event-title">{ev.title}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-white/70">
            <User className="w-4 h-4" /> {t('mymouvEvent.by', 'Par')} {ev.organizer?.name || ev.organizer?.username || '—'}
          </div>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm"><Calendar className="w-4 h-4 text-fuchsia-300" /> {ev.event_date}</div>
            <div className="flex items-center gap-1.5 text-sm"><Clock className="w-4 h-4 text-fuchsia-300" /> {ev.event_time}</div>
          </div>
          <div className="mt-3 flex items-start gap-1.5 text-sm">
            <MapPin className="w-4 h-4 text-fuchsia-300 mt-0.5" />
            <div>
              <div className="font-semibold">{ev.location?.name}</div>
              <div className="text-white/60 text-xs">{ev.location?.address}</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-white/80 whitespace-pre-wrap">{ev.description}</p>
        </div>

        {/* Map */}
        {hasCoords && (
          <div className="mt-6">
            <h2 className="text-lg font-bold mb-2">{t('mymouvEvent.location', 'Localisation')}</h2>
            <div className="rounded-2xl overflow-hidden" style={{ height: 280 }} data-testid="mymouv-event-map">
              <MapContainer center={[ev.location.lat, ev.location.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                <Marker position={[ev.location.lat, ev.location.lng]} icon={purpleIcon} />
              </MapContainer>
            </div>
            {/* Directions buttons — parity with MyShop detail */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${ev.location.lat},${ev.location.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm flex items-center justify-center gap-1.5 text-white"
                data-testid="mymouv-event-google-maps"
              >
                <ExternalLink className="w-4 h-4" /> {t('mymouvEvent.googleMaps', 'Google Maps')}
              </a>
              <a
                href={`https://maps.apple.com/?daddr=${ev.location.lat},${ev.location.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm flex items-center justify-center gap-1.5 text-white"
                data-testid="mymouv-event-apple-plans"
              >
                <ExternalLink className="w-4 h-4" /> {t('mymouvEvent.applePlans', 'Plans (Apple)')}
              </a>
            </div>
          </div>
        )}

        {/* Media gallery */}
        {(ev.images?.length > 0 || ev.video_url) && (
          <div className="mt-6" data-testid="mymouv-event-gallery">
            <h2 className="text-lg font-bold mb-2">{t('mymouvEvent.gallery', 'Galerie')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ev.video_url && (
                <button
                  type="button"
                  onClick={() => setFullscreen({ type: 'video', src: getMediaUrl(ev.video_url) })}
                  className="col-span-2 md:col-span-3 w-full rounded-2xl bg-black overflow-hidden relative group"
                  data-testid="mymouv-event-video-btn"
                >
                  <video
                    src={getMediaUrl(ev.video_url)}
                    playsInline
                    muted
                    className="w-full max-h-[60vh] object-contain bg-black pointer-events-none"
                    data-testid="mymouv-event-video"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40">
                    <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <div className="w-0 h-0 border-l-[18px] border-l-white border-y-[12px] border-y-transparent ml-1" />
                    </div>
                  </div>
                </button>
              )}
              {(ev.images || []).map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setFullscreen({ type: 'image', src: getMediaUrl(src) })}
                  className="rounded-2xl bg-white/5 overflow-hidden"
                  data-testid={`mymouv-event-image-btn-${i}`}
                >
                  <img
                    src={getMediaUrl(src)}
                    alt={`Photo ${i+1}`}
                    className="w-full aspect-square object-contain bg-black"
                    data-testid={`mymouv-event-image-${i}`}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Line-up */}
        {ev.lineup?.length > 0 && (
          <div className="mt-6">
            <h2 className="text-lg font-bold mb-2">{t('mymouvForm.lineupLabel', 'Line-up')}</h2>
            <div className="flex flex-wrap gap-2">
              {ev.lineup.map((a, idx) => (
                a.url ? (
                  <a key={idx} href={a.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm inline-flex items-center gap-1.5">
                    {a.name} <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : (
                  <span key={idx} className="px-3 py-1.5 rounded-full bg-white/10 text-sm">{a.name}</span>
                )
              ))}
            </div>
          </div>
        )}

        {/* Security */}
        {ev.security_measures && (
          <div className="mt-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-400/30">
            <h3 className="text-sm font-bold text-amber-300 mb-1">{t('mymouvForm.securityLabel', 'Mesures de sécurité')}</h3>
            <p className="text-sm text-white/80 whitespace-pre-wrap">{ev.security_measures}</p>
          </div>
        )}

        {/* Tickets */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h2 className="text-lg font-bold">{t('mymouvEvent.tickets', 'Billets')}</h2>
            <div className="flex items-center gap-3">
              <button onClick={() => { setPromoOpen(true); setPromoTtypeId((ev.ticket_types || [])[0]?.id || ''); }} className="text-xs px-3 py-1.5 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 inline-flex items-center gap-1.5" data-testid="mymouv-promo-open-btn">
                <Tag className="w-3.5 h-3.5" /> {t('mymouvEvent.havePromo', "J'ai un code promo")}
              </button>
              <span className="text-sm text-white/60 flex items-center gap-1"><Ticket className="w-4 h-4" /> {ev.tickets_remaining} / {ev.tickets_total} {t('mymouvEvent.available', 'disponibles')}</span>
            </div>
          </div>
          <div className="space-y-3">
            {(ev.ticket_types || []).map(tt => (
              <div key={tt.id} className={`p-4 rounded-2xl border ${tt.is_vip ? 'border-yellow-400/50 bg-yellow-400/5' : 'border-fuchsia-500/40 bg-fuchsia-500/5'}`} data-testid={`mymouv-ticket-type-${tt.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold flex items-center gap-2">
                      {tt.name}
                      {tt.is_vip && <span className="px-2 py-0.5 rounded-full bg-yellow-400 text-black text-[10px] font-bold">VIP</span>}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">{tt.quantity} {t('mymouvEvent.seats', 'places')}</div>
                  </div>
                  <div className="text-xl font-bold">
                    {Number(tt.price) > 0 ? `${tt.price}${currencySymbol(tt.currency)}` : t('mymouvHome.free', 'Gratuit')}
                  </div>
                </div>
                <button
                  onClick={() => buy(tt.id)}
                  disabled={buying === tt.id || ev.tickets_remaining === 0}
                  className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 disabled:opacity-50 text-white font-semibold text-sm"
                  data-testid={`mymouv-buy-btn-${tt.id}`}
                >
                  {buying === tt.id ? t('mymouvEvent.redirecting', 'Redirection…') : ev.tickets_remaining === 0 ? t('mymouvHome.soldOut', 'Complet') : (Number(tt.price) > 0 ? t('mymouvEvent.buyBtn', 'Acheter') : t('mymouvEvent.reserveBtn', 'Réserver'))}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* External link + socials */}
        {ev.external_link_url && (
          <a href={ev.external_link_url} target="_blank" rel="noopener noreferrer" className="mt-6 block p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-sm">
            🔗 {ev.external_link_title || t('mymouvEvent.moreInfo', "Plus d'infos")} <ExternalLink className="w-3 h-3 inline ml-1" />
          </a>
        )}

        {/* v49.4 — Accreditation request form (after external link) */}
        {ev.allow_accreditations && (
          <AccreditationRequestForm eventId={ev.id} />
        )}
      </div>

      {/* Fullscreen media viewer */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-3"
          onClick={() => setFullscreen(null)}
          data-testid="mymouv-fullscreen"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setFullscreen(null); }}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md z-10"
            data-testid="mymouv-fullscreen-close"
          >
            <X className="w-6 h-6" />
          </button>
          {fullscreen.type === 'video' ? (
            <video
              src={fullscreen.src}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="mymouv-fullscreen-video"
            />
          ) : (
            <img
              src={fullscreen.src}
              alt="Aperçu plein écran"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="mymouv-fullscreen-image"
            />
          )}
        </div>
      )}

      {/* Promo code redemption dialog — keyboard-aware, always centered */}
      {promoOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
          data-testid="mymouv-promo-dialog"
          onClick={() => setPromoOpen(false)}
          style={{
            paddingBottom: keyboardHeight > 0 ? `${keyboardHeight + 16}px` : undefined,
            alignItems: keyboardHeight > 0 ? 'flex-start' : 'center',
            paddingTop: keyboardHeight > 0 ? '20px' : undefined,
            transition: 'padding 180ms ease-out',
          }}
        >
          <div
            className="bg-gray-900 rounded-2xl border border-white/10 p-6 max-w-md w-full max-h-[calc(100dvh-40px)] overflow-y-auto"
            style={{
              paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2"><Tag className="w-5 h-5 text-fuchsia-400" /> {t('mymouvEvent.usePromo', 'Utiliser un code promo')}</h3>
              <button onClick={() => setPromoOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 text-white/70">✕</button>
            </div>
            <p className="text-xs text-white/60 mb-3">{t('mymouvEvent.promoHint', 'Un code promo offre 1 billet gratuit (usage unique).')}</p>
            <label className="block text-xs text-white/60 mb-1">{t('mymouvEvent.ticketType', 'Type de billet')}</label>
            <select
              value={promoTtypeId}
              onChange={e => setPromoTtypeId(e.target.value)}
              className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
              data-testid="mymouv-promo-ttype-select"
            >
              {(ev.ticket_types || []).map(tt => (
                <option key={tt.id} value={tt.id}>{tt.name}{tt.is_vip ? ' · VIP' : ''}</option>
              ))}
            </select>
            <label className="block text-xs text-white/60 mb-1">{t('mymouvEvent.promoCodeLabel', 'Code promo')}</label>
            <input
              value={promoCode}
              onChange={e => setPromoCode(e.target.value.toUpperCase())}
              onFocus={(e) => {
                // Ensure the input stays visible above the keyboard on iOS/Android
                setTimeout(() => e.target?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 200);
              }}
              placeholder="ABCDEFGH"
              className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-white text-sm font-mono tracking-widest"
              data-testid="mymouv-promo-code-input"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setPromoOpen(false)} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm">{t('mymouvForm.cancelBtn', 'Annuler')}</button>
              <button onClick={redeemPromo} disabled={promoBusy} className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="mymouv-promo-redeem-submit">
                {promoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                {t('mymouvEvent.validateBtn', 'Valider')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report dialog */}
      {reporting && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={() => setReporting(false)}
        >
          <div
            className="bg-gray-900 rounded-2xl border border-white/10 p-6 max-w-md w-full max-h-[90dvh] overflow-y-auto"
            style={{
              paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
              transition: 'margin-bottom 180ms ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg mb-3">{t('mymouvEvent.reportTitle', 'Signaler cet événement')}</h3>
            <textarea
              value={reportReason}
              onChange={e => setReportReason(e.target.value)}
              rows={4}
              placeholder={t('mymouvEvent.reportPlaceholder', 'Précisez la raison…')}
              className="w-full bg-white/10 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
              data-testid="mymouv-report-reason"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setReporting(false)} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm">{t('mymouvForm.cancelBtn', 'Annuler')}</button>
              <button onClick={submitReport} className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold" data-testid="mymouv-report-submit">{t('mymouvEvent.sendBtn', 'Envoyer')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMouvEventDetail;
